import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Package, Users, Settings, X, DollarSign, Wallet, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePartnerAuth, PartnerPermission } from '@/hooks/usePartnerAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import HelpSupportDialog from '@/components/shared/HelpSupportDialog';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  permission?: PartnerPermission;
  ownerOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/partner/dashboard' },
  { label: 'Orders', icon: Package, path: '/partner/orders' },
  { label: 'My Pricing', icon: DollarSign, path: '/partner/pricing', permission: 'view_earnings' },
  { label: 'My Earnings', icon: Wallet, path: '/partner/earnings', permission: 'view_earnings' },
  { label: 'Team', icon: Users, path: '/partner/team', permission: 'view_team' },
  { label: 'Settings', icon: Settings, path: '/partner/settings', permission: 'manage_settings' },
];

interface PartnerSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const PartnerSidebar = ({ isOpen, onClose }: PartnerSidebarProps) => {
  const { isOwner, hasPermission, partner } = usePartnerAuth();
  const isMobile = useIsMobile();
  const [helpOpen, setHelpOpen] = useState(false);

  const filteredItems = navItems.filter(item => {
    // Items without permission requirement are always shown
    if (!item.permission && !item.ownerOnly) return true;
    // Owner-only items
    if (item.ownerOnly) return isOwner;
    // Permission-based items
    if (item.permission) return hasPermission(item.permission);
    return true;
  });

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b md:hidden">
        <span className="font-semibold">Menu</span>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {filteredItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={isMobile ? onClose : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      
      {/* Help & Support */}
      <div className="p-4 border-t">
        <Button 
          variant="ghost" 
          className="w-full justify-start text-muted-foreground hover:text-foreground"
          onClick={() => setHelpOpen(true)}
        >
          <HelpCircle className="h-5 w-5 mr-3" />
          Help & Support
        </Button>
      </div>
    </div>
  );

  // Mobile sidebar with overlay
  if (isMobile) {
    return (
      <>
        {/* Overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={onClose}
          />
        )}
        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 bg-background border-r transform transition-transform duration-200 ease-in-out',
            isOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {sidebarContent}
        </aside>
      </>
    );
  }

  // Desktop sidebar
  return (
    <>
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 border-r bg-background">
        <div className="h-16 border-b" /> {/* Spacer for header */}
        {sidebarContent}
      </aside>
      
      <HelpSupportDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        portalType="partner"
        companyId={partner?.id}
        companyName={partner?.name}
      />
    </>
  );
};

export default PartnerSidebar;
